import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { KeyRound, Loader2, ToggleLeft, ToggleRight } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Owner-only token-gate configurator (flag-gated REACT_APP_MICROTX). Writes PUT /bloqs/{id}/gate
// (GateSetIn: type none|token|nft, chain, contract, min_balance, token_id). Degrades gracefully
// (hidden) when the backend endpoint isn't live yet.
const CHAINS = [
  { id: "ethereum", label: "Ethereum" },
  { id: "base", label: "Base" },
  { id: "arbitrum", label: "Arbitrum" },
  { id: "optimism", label: "Optimism" },
  { id: "polygon", label: "Polygon" },
  { id: "bsc", label: "BNB Chain" },
  { id: "avalanche", label: "Avalanche" },
  { id: "linea", label: "Linea" },
  { id: "scroll", label: "Scroll" },
  { id: "fantom", label: "Fantom" },
  { id: "krown", label: "KROWN Network" },
  { id: "solana", label: "Solana" },
];
const CHAIN_LABEL = Object.fromEntries(CHAINS.map((c) => [c.id, c.label]));
const short = (a) => (a && a.length > 14 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a);

const PENDING = [404, 405, 501];

export function TokenGateSection({ bloq, invalidate, open }) {
  const qc = useQueryClient();
  const { data: gate, error } = useQuery({
    queryKey: ["bloq-gate", bloq.id],
    queryFn: async () => (await api.bloqGate(bloq.id))?.gate ?? null,
    enabled: open && !!bloq.id,
    retry: false,
  });

  const [enabled, setEnabled] = useState(false);
  const [type, setType] = useState("token");
  const [chain, setChain] = useState("ethereum");
  const [contract, setContract] = useState("");
  const [minBalance, setMinBalance] = useState("1");
  const [tokenId, setTokenId] = useState("");
  const [busy, setBusy] = useState(false);

  // Seed the form from the live gate once it loads.
  useEffect(() => {
    if (!gate) return;
    const t = gate.type && gate.type !== "none" ? gate.type : null;
    setEnabled(!!t);
    if (t) setType(t === "nft" ? "nft" : "token");
    if (gate.chain) setChain(gate.chain);
    if (gate.contract) setContract(gate.contract);
    if (gate.min_balance != null) setMinBalance(String(gate.min_balance));
    if (gate.token_id != null) setTokenId(String(gate.token_id));
  }, [gate]);

  const notLive = PENDING.includes(error?.response?.status);
  const hasGate = !!gate;

  const preview = useMemo(() => {
    if (!enabled) return "Anyone can join this BLOQ.";
    const on = ` on ${CHAIN_LABEL[chain] || chain}`;
    const c = contract ? ` (${short(contract)})` : "";
    if (type === "nft") {
      return tokenId.trim()
        ? `Hold NFT #${tokenId.trim()} from${c ? "" : " the collection"}${c}${on}`
        : `Hold ${Number(minBalance) || 1}+ NFT(s) from the collection${c}${on}`;
    }
    return `Hold ${Number(minBalance) || 1}+ tokens${c}${on}`;
  }, [enabled, type, chain, contract, minBalance, tokenId]);

  const save = async () => {
    if (busy) return;
    let payload;
    if (!enabled) {
      payload = { type: "none" };
    } else {
      if (!chain) return toast.error("Pick a chain.");
      if (!contract.trim()) return toast.error("Enter the token/collection contract address.");
      const mb = Number(minBalance);
      if (!mb || mb <= 0) return toast.error("Minimum must be greater than 0.");
      payload = { type, chain, contract: contract.trim(), min_balance: mb };
      if (type === "nft" && tokenId.trim()) payload.token_id = tokenId.trim();
    }
    setBusy(true);
    try {
      await api.setBloqGate(bloq.id, payload);
      toast.success(enabled ? "Token gate saved" : "Token gate removed");
      qc.invalidateQueries({ queryKey: ["bloq-gate", bloq.id] });
      invalidate?.();
    } catch (e) {
      const st = e?.response?.status;
      const d = e?.response?.data?.detail;
      if (PENDING.includes(st)) toast.info("Token gating isn't live on this server yet — it'll activate after the next deploy.");
      else if (st === 403) toast.error("Only the BLOQ owner can set a token gate.");
      else toast.error((typeof d === "string" && d) || d?.message || "Couldn't update the token gate.");
    } finally {
      setBusy(false);
    }
  };

  if (notLive) return null; // backend not deployed here yet — stay hidden

  return (
    <div className="mt-6" data-testid="bloq-token-gate-section">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-bloq-muted">
        <KeyRound className="h-3.5 w-3.5 text-bloq-purple" /> Token gate
      </div>
      <div className="rounded-2xl border border-white/8 bg-bloq-s2 p-4">
        <button
          onClick={() => setEnabled((v) => !v)}
          className="flex w-full items-center justify-between"
          data-testid="token-gate-toggle"
        >
          <div className="text-left">
            <div className="text-sm font-medium">Require an on-chain asset to join</div>
            <div className="text-xs text-bloq-muted">Members prove ownership with a read-only signature</div>
          </div>
          {enabled ? <ToggleRight className="h-6 w-6 text-bloq-purple" /> : <ToggleLeft className="h-6 w-6 text-bloq-muted" />}
        </button>

        {enabled && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-2" data-testid="token-gate-type">
              {[
                { id: "token", label: "Token" },
                { id: "nft", label: "NFT collection" },
              ].map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setType(o.id)}
                  className={cn(
                    "rounded-xl border py-2.5 text-sm font-bold transition-colors",
                    type === o.id ? "border-bloq-purple/60 bg-bloq-purple/15 text-bloq-purple" : "border-white/10 text-bloq-muted hover:text-white",
                  )}
                  data-testid={`token-gate-type-${o.id}`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-bloq-muted">Chain</Label>
              <Select value={chain} onValueChange={setChain}>
                <SelectTrigger className="h-11 rounded-xl border-white/12 bg-bloq-bg" data-testid="token-gate-chain">
                  <SelectValue placeholder="Select chain" />
                </SelectTrigger>
                <SelectContent>
                  {CHAINS.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`token-gate-chain-${c.id}`}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-bloq-muted">
                {type === "nft" ? "Collection contract" : "Token contract"}
              </Label>
              <Input
                value={contract}
                onChange={(e) => setContract(e.target.value)}
                placeholder={chain === "solana" ? "Mint address" : "0x…"}
                className="h-11 rounded-xl border-white/12 bg-bloq-bg font-mono text-sm"
                data-testid="token-gate-contract"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-bloq-muted">
                  {type === "nft" ? "Min held" : "Min balance"}
                </Label>
                <Input
                  type="number"
                  min="0"
                  step={type === "nft" ? "1" : "any"}
                  value={minBalance}
                  onChange={(e) => setMinBalance(e.target.value)}
                  className="h-11 rounded-xl border-white/12 bg-bloq-bg"
                  data-testid="token-gate-min"
                />
              </div>
              {type === "nft" && (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-bloq-muted">Token ID (optional)</Label>
                  <Input
                    value={tokenId}
                    onChange={(e) => setTokenId(e.target.value)}
                    placeholder="Any"
                    className="h-11 rounded-xl border-white/12 bg-bloq-bg"
                    data-testid="token-gate-token-id"
                  />
                </div>
              )}
            </div>

            <div className="rounded-xl border border-bloq-purple/25 bg-bloq-purple/[0.06] p-3" data-testid="token-gate-preview">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-bloq-purple">Members will need to</div>
              <div className="mt-0.5 text-sm font-medium text-white">{preview}</div>
            </div>
          </div>
        )}

        <Button
          onClick={save}
          disabled={busy || (!enabled && !hasGate)}
          className="mt-3 w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90 disabled:opacity-40"
          data-testid="token-gate-save"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : enabled ? "Save token gate" : "Remove token gate"}
        </Button>
        <p className="mt-2 text-[11px] text-bloq-muted">On-chain checks run across 11 chains. Ownership is verified with a signature — never a transaction.</p>
      </div>
    </div>
  );
}
